import { useAuth0 } from "@auth0/auth0-react";
import { UserClaims } from "common/models";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import { FullScreenLoader } from "@app.automotus.io/components/FullScreenLoader";
import { Navigate } from "react-router-dom";
import React, { useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import FormHelperText from "@mui/material/FormHelperText";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import LoginEmailIcon from "@app.automotus.io/components/icons/LoginEmailIcon";
import LogoBlue from "@app.automotus.io/components/images/AppLogo/logoBlue";

const CHECK_SSO_CONNECTION = gql`
  query CheckSsoConnection($email: String!) {
    ssoConnection: auth_check_sso_connection(args: { email: $email }) {
      is_sso_enabled
      connection
    }
  }
`;

interface CheckSsoConnectionData {
  ssoConnection: {
    is_sso_enabled: boolean | null;
    connection: string | null;
  };
}

interface CheckSsoConnectionVars {
  email: string;
}

export const CurbsuiteLogin: React.FC = () => {
  const { userProfile, loading } = useUserProfile();
  return (
    <FullScreenLoader loading={loading}>
      {userProfile ? <Navigate to={"/dashboard"} /> : <EmailFirstLogin />}
    </FullScreenLoader>
  );
};

const EmailFirstLogin: React.FC = () => {
  const { loginWithRedirect } = useAuth0<UserClaims>();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [redirectingToUniversalLogin, setRedirectingToUniversalLogin] = useState(false);

  const [checkSsoConnection, { loading }] = useLazyQuery<CheckSsoConnectionData, CheckSsoConnectionVars>(
    CHECK_SSO_CONNECTION,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }

    // Plus-addressed emails (user+tag@domain) bypass SSO — Auth0 can't match
    // them to an IdP identity, so route straight to username/password login.
    const [localPart] = normalizedEmail.split("@");
    if (localPart.includes("+")) {
      setRedirectingToUniversalLogin(true);
      await loginWithRedirect({ login_hint: normalizedEmail });
      return;
    }

    try {
      const { data } = await checkSsoConnection({ variables: { email: normalizedEmail } });
      const ssoResult = data?.ssoConnection;

      if (ssoResult?.is_sso_enabled && ssoResult.connection) {
        await loginWithRedirect({
          connection: ssoResult.connection,
          login_hint: normalizedEmail,
          redirectUri: `${window.location.origin}/authorize`,
        });
      } else {
        setRedirectingToUniversalLogin(true);
        await loginWithRedirect({ login_hint: normalizedEmail });
      }
    } catch {
      // SSO check failed — fall back to standard login so non-SSO users are never blocked
      setRedirectingToUniversalLogin(true);
      await loginWithRedirect({ login_hint: normalizedEmail });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: "100%",
        minHeight: "100vh",
        backgroundColor: "#1c1c1c",
      }}
    >
      <Card sx={{ width: 350, borderRadius: "4px", boxShadow: 6 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 1.375,
            backgroundColor: "#f1f1f1",
          }}
        >
          <LogoBlue height={31} width="auto" sx={{ mb: 1 }} />
          <Typography
            sx={{
              fontSize: "22px",
              lineHeight: "30px",
              fontFamily: `"Avenir Next", Avenir, -apple-system, "system-ui", Roboto, Helvetica, sans-serif`,
            }}
          >
            Sign in to CurbSuite
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ my: 3, px: 2.5, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                border: "1px solid",
                borderColor: error ? "error.main" : "rgba(0, 0, 0, 0.5)",
                borderRadius: "4px",
                overflow: "hidden",
                height: "42px",
                "&:focus-within": {
                  borderColor: error ? "error.main" : "#635dff",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                  backgroundColor: "#f5f5f5",
                  flexShrink: 0,
                }}
              >
                <LoginEmailIcon />
              </Box>
              <InputBase
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{ px: 1.5, py: 1 }}
                inputProps={{
                  "aria-label": "Email",
                  "aria-invalid": !!error,
                  "aria-describedby": error ? "email-error" : undefined,
                  style: { fontSize: "13px" },
                }}
              />
            </Box>
            {error && (
              <FormHelperText id="email-error" error role="alert">
                {error}
              </FormHelperText>
            )}
            {redirectingToUniversalLogin && (
              <Box role="status" sx={{ alignSelf: "center", mt: 2 }}>
                <Typography fontWeight="600" color="primary.main">
                  Redirecting to universal login...
                </Typography>
              </Box>
            )}
          </Box>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth
            loading={submitting}
            disabled={!email.trim()}
            sx={{
              backgroundColor: "#635dff",
              textTransform: "uppercase",
              height: "70px",
              fontWeight: 600,
              borderRadius: "3px",
              "&:hover": { backgroundColor: "#4a45d6" },
            }}
          >
            Continue
          </LoadingButton>
        </Box>
      </Card>
    </Box>
  );
};

export default CurbsuiteLogin;
